import React, { useState, useRef, useEffect } from "react";
import { rotulo, rotuloLista } from "../utils/rotulos";

/**
 * Compartilhar a vaga com o texto JÁ PRONTO.
 *
 * Por que existe: quem quer indicar uma vaga a um amigo copia o link cru — e link cru no WhatsApp vira uma
 * linha sem contexto que ninguém abre. Aqui o texto sai montado (cargo, empresa, local, skills, link), então
 * cada candidato e cada empresa viram distribuição. É o canal de alcance mais barato que a plataforma tem.
 *
 * O LinkedIn ignora texto pré-preenchido desde 2021 (só aceita a URL). Em vez de fingir que funciona, a gente
 * copia o texto para a área de transferência ANTES de abrir a janela e avisa a pessoa para colar — é o único
 * caminho honesto.
 */
export default function CompartilharVaga({ vaga, className = "", compacto = false }) {
  const [copiado, setCopiado] = useState(null);
  const [aberto, setAberto] = useState(false);

  // ---- POSIÇÃO DO MENU DE COMPARTILHAR (modo compacto, usado na lista do mural) ----
  //
  // O menu é ancorado por COORDENADAS DE TELA (position: fixed), não dentro do card.
  //
  // Motivo: a lista do mural vive num container com `overflow-x-auto` — e o navegador, quando um dos eixos
  // deixa de ser `visible`, passa a recortar o outro também. Com muitas vagas o problema não aparecia, porque
  // sobrava altura embaixo; com UMA vaga só, o menu nascia maior que o container e ficava preso lá dentro,
  // sem como ser clicado (relatado em 03/09/2026, com a busca filtrando uma única vaga).
  //
  // Ancorar na tela resolve para qualquer quantidade de vagas e evita abrir mão do scroll horizontal da
  // tabela, que existe para telas estreitas.
  //
  // ⚠️ Os hooks ficam AQUI, antes do `if (compacto)`: dentro dele seriam chamados condicionalmente, o que o
  // React proíbe (e o build acusa como erro, não como aviso).
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);
  const LARGURA_MENU = 176;   // w-44
  const ALTURA_MENU = 160;    // altura aproximada, só para decidir se abre para cima ou para baixo

  const abrirMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      // Cabe embaixo? Se não, sobe: melhor um menu acima do botão do que um menu fora da tela.
      const paraCima = r.bottom + ALTURA_MENU > window.innerHeight;
      setPos({
        top: paraCima ? Math.max(8, r.top - ALTURA_MENU - 4) : r.bottom + 4,
        left: Math.max(8, Math.min(r.right - LARGURA_MENU, window.innerWidth - LARGURA_MENU - 8)),
      });
    }
    setAberto((x) => !x);
  };

  // Menu ancorado na tela não acompanha a rolagem: em vez de deixá-lo flutuando descolado do botão, fecha.
  useEffect(() => {
    if (!aberto) return;
    const fechar = () => setAberto(false);
    window.addEventListener("scroll", fechar, true);
    window.addEventListener("resize", fechar);
    return () => {
      window.removeEventListener("scroll", fechar, true);
      window.removeEventListener("resize", fechar);
    };
  }, [aberto]);

  // O link vai para LinkedIn, e-mail e grupo de WhatsApp: "/vagas/74" não diz nada a quem lê nem ao buscador.
  // Cai no endereço por Id só enquanto a vaga ainda não tem slug — link errado é pior que link feio.
  const url = `${window.location.origin}${vaga.url || `/vagas/${vaga.id}`}`;
  const local = vaga.remoto || vaga.modalidade === "Remoto"
    ? "Remoto"
    : [vaga.cidade, rotulo(vaga.modalidade)].filter(Boolean).join(" · ");
  const linhaDetalhe = [vaga.senioridade, local, vaga.tipoContratacao].filter(Boolean).join(" · ");
  const skills = (vaga.skills || []).slice(0, 5).join(", ");
  const salario = vaga.salarioMin != null || vaga.salarioMax != null
    ? `${vaga.salarioMin != null && vaga.salarioMax != null
        ? `R$ ${vaga.salarioMin.toLocaleString("pt-BR")} a R$ ${vaga.salarioMax.toLocaleString("pt-BR")}`
        : `a partir de R$ ${(vaga.salarioMin ?? vaga.salarioMax).toLocaleString("pt-BR")}`}`
    : null;

  // SEM EMOJI, de propósito: o protocolo whatsapp:// do Windows não preserva caracteres fora do BMP
  // (emoji são pares substitutos) e eles chegam ao destinatário como losangos pretos. "·" e "—" são BMP e passam.
  const texto = [
    `${vaga.titulo} — ${vaga.empresa}`,
    linhaDetalhe,
    skills && `Skills: ${skills}`,
    salario && `Salário: ${salario}`,
    "",
    "Vaga aberta no eaiRH — dá pra ver a reputação da empresa em processos seletivos antes de se candidatar:",
    url,
  ].filter((l) => l !== null && l !== undefined && l !== false).join("\n").replace(/\n{3,}/g, "\n\n");

  const copiar = async (marca) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(marca);
      setTimeout(() => setCopiado(null), 3000);
      return true;
    } catch {
      return false;
    }
  };

  const noWhatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, "_blank", "noopener");

  const noLinkedin = async () => {
    // Copia primeiro: a janela do LinkedIn abre só com a URL, e a pessoa cola o texto no campo do post.
    await copiar("linkedin");
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank", "noopener,width=640,height=640");
  };

  // No celular o menu nativo é o caminho curto (Telegram, Instagram, e-mail, o que a pessoa tiver).
  const nativo = () => navigator.share({ title: `${vaga.titulo} — ${vaga.empresa}`, text: texto, url }).catch(() => {});

  const btn = "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors";

  // MODO COMPACTO (linha do mural): a lista tem 21 vagas — três botões por linha viraria ruído. Um ícone
  // discreto abre o menu. A linha inteira é clicável, então tudo aqui precisa segurar o clique (stopPropagation),
  // senão compartilhar navegaria para a vaga no mesmo gesto.
  if (compacto) {
    const segura = (fn) => (e) => { e.stopPropagation(); fn(); };

    return (
      <div className={`relative ${className}`} onClick={(e) => e.stopPropagation()}>
        <button ref={btnRef} onClick={segura(abrirMenu)} aria-label={`Compartilhar a vaga ${vaga.titulo}`}
          title="Compartilhar esta vaga"
          className="rounded-lg border border-gray-200 px-2 py-1 text-gray-400 hover:text-primary hover:border-primary hover:bg-blue-50">
          ↗
        </button>
        {aberto && (
          <>
            {/* Camada invisível: clicar fora fecha, sem prender o usuário no menu. */}
            <div className="fixed inset-0 z-40" onClick={segura(() => setAberto(false))} />
            <div style={pos ? { top: pos.top, left: pos.left } : undefined}
              className="fixed z-50 w-44 rounded-xl border border-gray-200 bg-white py-1 shadow-lg text-left">
              <button onClick={segura(() => { noWhatsapp(); setAberto(false); })}
                className="block w-full px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-green-50">💬 WhatsApp</button>
              <button onClick={segura(() => { noLinkedin(); setAberto(false); })}
                className="block w-full px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-blue-50">in LinkedIn</button>
              <button onClick={segura(() => copiar("texto"))}
                className="block w-full px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                {copiado === "texto" ? "✓ copiado" : "📋 Copiar texto"}
              </button>
              {typeof navigator !== "undefined" && navigator.share && (
                <button onClick={segura(() => { nativo(); setAberto(false); })}
                  className="block w-full px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">↗ Mais</button>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-gray-200 bg-white p-3 ${className}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-gray-500 mr-1">Conhece alguém pra esta vaga?</span>
        <button onClick={noWhatsapp} className={`${btn} border-green-300 bg-green-50 text-green-800 hover:bg-green-100`}>
          <span aria-hidden>💬</span> WhatsApp
        </button>
        <button onClick={noLinkedin} className={`${btn} border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100`}>
          <span aria-hidden>in</span> LinkedIn
        </button>
        <button onClick={() => copiar("texto")} className={`${btn} border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100`}>
          <span aria-hidden>📋</span> Copiar texto
        </button>
        {typeof navigator !== "undefined" && navigator.share && (
          <button onClick={nativo} className={`${btn} border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100`}>
            <span aria-hidden>↗</span> Mais
          </button>
        )}
      </div>
      {copiado === "texto" && <p role="status" className="mt-2 text-xs text-green-700">Texto copiado — é só colar onde quiser.</p>}
      {copiado === "linkedin" && (
        <p role="status" className="mt-2 text-xs text-blue-800">
          Texto copiado. O LinkedIn não aceita texto pronto pela janela — <strong>cole (Ctrl+V)</strong> no post que abriu.
        </p>
      )}
      {copiado === null && (
        <p className="mt-2 text-[11px] text-gray-400">O texto vai pronto com cargo, local, skills e o link da vaga.</p>
      )}
    </div>
  );
}
